import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";

import { Paragraph, Select, type SelectItem, Small } from "@fronterahealth/frontera-ui-components";

import {
  type PreviewQARulesInput,
  type QAPreviewPackage,
  type QAPreviewRule,
  previewQaRules,
} from "@api/rest/types-and-hooks";
import { PreviewPackageRow } from "@pages/QARules/PreviewPackageRow";
import { StillBadge } from "@pages/QARules/StillBadge";
import { QA_RULE_PREVIEW_QUERY_KEY } from "@pages/QARules/constants";
import { ensureOk, humanizeEnum } from "@pages/QARules/qaRuleLabels";
import { useQAPickerOptions } from "@pages/QARules/useQAPickerOptions";

const NO_ORG: SelectItem = { id: "", primary: "No organization" };
const NO_TEMPLATE: SelectItem = { id: "", primary: "No template" };

interface RulePreviewProps {
  /** Locks the organization (org detail page) and hides its picker. */
  fixedOrganizationId?: number;
  /** Limits the organization picker to these orgs (e.g. a template's assigned orgs). */
  organizationChoices?: readonly { id: number; name: string }[];
  /** Locks the template (template detail page) and hides its picker. */
  fixedTemplateId?: number;
  renderPackageAction?: (pkg: QAPreviewPackage) => ReactNode;
  renderRuleAction?: (pkg: QAPreviewPackage, rule: QAPreviewRule) => ReactNode;
}

/** "I'm doing this report — which QA packages and rules get pulled in, and why?" */
export const RulePreview: React.FC<RulePreviewProps> = ({
  fixedOrganizationId,
  organizationChoices,
  fixedTemplateId,
  renderPackageAction,
  renderRuleAction,
}) => {
  const templateFixed = fixedTemplateId !== undefined;
  const orgFixed = fixedOrganizationId !== undefined;
  const { organizationItems, orgsLoading, templateItems, templatesLoading, setTemplateSearch } = useQAPickerOptions({
    needOrganizations: !orgFixed && !organizationChoices,
    needTemplates: !templateFixed,
    templateOrganizationId: fixedOrganizationId,
  });

  const [pickedOrganization, setOrganization] = useState<SelectItem>(NO_ORG);
  const [template, setTemplate] = useState<SelectItem>(NO_TEMPLATE);

  const orgItems: SelectItem[] = organizationChoices
    ? [...organizationChoices]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((org) => ({ id: String(org.id), primary: org.name }))
    : organizationItems;
  // An org picked earlier may have since been unassigned from the template.
  const organization = orgItems.some((item) => item.id === pickedOrganization.id) ? pickedOrganization : NO_ORG;
  const input: PreviewQARulesInput = {
    organization_id: fixedOrganizationId ?? (organization.id ? Number(organization.id) : null),
    report_template_id: fixedTemplateId ?? (template.id ? Number(template.id) : null),
  };

  const {
    data: result,
    isFetching,
    error,
  } = useQuery({
    queryKey: [QA_RULE_PREVIEW_QUERY_KEY, input],
    queryFn: async () => {
      const response = ensureOk(await previewQaRules(input), "Could not run preview.");
      return response.status === 200 ? response.data : null;
    },
  });

  const sentCount = result?.packages.reduce((n, pkg) => n + pkg.rules.filter((r) => r.included).length, 0) ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4">
        {!orgFixed && (
          <Select
            title="Organization"
            items={[NO_ORG, ...orgItems]}
            placeholderText="No organization"
            enableSearch
            isLoading={orgsLoading}
            selected={organization}
            setSelected={setOrganization}
          />
        )}
        {!templateFixed && (
          <Select
            title="Template"
            items={[NO_TEMPLATE, ...templateItems]}
            placeholderText="Search templates"
            enableSearch
            onSearch={setTemplateSearch}
            isLoading={templatesLoading}
            selected={template}
            setSelected={setTemplate}
          />
        )}
      </div>

      {error && <Paragraph className="text-text-error-primary">{error.message}</Paragraph>}
      {isFetching && !result && <Small colorType="secondary">Resolving...</Small>}

      {result && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Small colorType="secondary">
              {sentCount} rule{sentCount === 1 ? "" : "s"} sent, resolved for
            </Small>
            <StillBadge
              appearance="neutral"
              text={result.report_type ? humanizeEnum(result.report_type) : "Any report type"}
            />
            <StillBadge appearance="neutral" text={result.payor_type ? humanizeEnum(result.payor_type) : "No payor"} />
          </div>
          {result.packages.length === 0 ? (
            <Paragraph className="text-text-secondary">No packages apply.</Paragraph>
          ) : (
            <div className="flex flex-col gap-2">
              {result.packages.map((pkg) => (
                <PreviewPackageRow
                  key={pkg.id}
                  pkg={pkg}
                  packageAction={renderPackageAction?.(pkg)}
                  renderRuleAction={renderRuleAction ? (rule) => renderRuleAction(pkg, rule) : undefined}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
